<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import MinMaxOffsetBreached = Modules.BookingExperts.Data.MinMaxOffsetBreachedDTO;
import { useApiClient } from "@/api/client";
import { formatNumber } from "chartjs-graph";
import BexAdministrationDO = Modules.BookingExperts.Data.BexAdministrationDO;

const api = useApiClient("bookingExperts");
const isLoading = ref<boolean>(false);
const items = ref<MinMaxOffsetBreached[]>([]);

const administrations = ref<BexAdministrationDO[]>([]);

const fetchAdministrations = async () => {
	const response = await useApiClient("bookingExperts").getAdministrations();
	if (!response.isOk()) return;

	administrations.value = response.value;
};

onBeforeMount(async () => {
	await fetchMinMaxOffsetBreachCounts();
	await fetchAdministrations();
});

const fetchMinMaxOffsetBreachCounts = async () => {
	isLoading.value = true;
	const response = await api.getMinMaxOffsetBreaches();
	if (response.isOk()) {
		items.value = response.value.sort(
			(a, b) =>
				b.maxOffsetCount +
				b.minOffsetCount -
				(a.maxOffsetCount + a.minOffsetCount)
		);
	}
	isLoading.value = false;
};

const showAdministrationColumn = computed(
	() => administrations.value.length > 1
);

const headers = computed(() => [
	...(showAdministrationColumn.value
		? [
				{
					title: "Administration",
					align: "start" as const,
					key: "administrationName",
					sortable: false,
				},
		  ]
		: []),
	{
		title: "Pricelist",
		align: "start" as const,
		key: "pricelistName",
		sortable: false,
	},
	{
		title: "Max offset breaches",
		align: "end" as const,
		key: "maxOffsetCount",
		sortable: false,
	},
	{
		title: "Min offset breaches",
		align: "end" as const,
		key: "minOffsetCount",
		sortable: false,
	},
	{
		title: "Go to pricelist",
		align: "end" as const,
		key: "pricelistId",
		sortable: false,
	},
]);
</script>

<template>
	<v-card class="home-graph-card">
		<v-card-title class="bg-background" style="height: 48px">
			Min/max offset breaches
		</v-card-title>

		<v-card-text class="px-2 py-0">
			<v-data-table
				density="compact"
				:headers="headers"
				:items="items"
				:loading="isLoading"
				:items-per-page="4"
				hide-default-header
				:hide-default-footer="!items.length"
			>
				<template #no-data>
					<div class="my-3">There are currently no min/max offset breaches</div>
				</template>

				<template #item.maxOffsetCount="{ item }">
					<v-chip
						:prepend-icon="item.maxOffsetCount ? 'mdi-arrow-up' : ''"
						density="compact"
						:color="item.maxOffsetCount ? 'success' : 'secondary-tonal'"
						:style="`opacity: ${item.maxOffsetCount ? '1' : '0.5'}`"
					>
						{{ formatNumber(item.maxOffsetCount) }}
					</v-chip>
				</template>
				<template #item.minOffsetCount="{ item }">
					<v-chip
						:prepend-icon="item.maxOffsetCount ? 'mdi-arrow-down' : ''"
						density="compact"
						:color="item.maxOffsetCount ? 'error' : 'secondary-tonal'"
						:style="`opacity: ${item.maxOffsetCount ? '1' : '0.5'}`"
					>
						{{ formatNumber(item.minOffsetCount) }}
					</v-chip>
				</template>
				<template #item.pricelistId="{ item }">
					<v-btn
						v-tooltip="
							`Show offset breaches for pricelist \'${item.pricelistName}\'`
						"
						size="30"
						:to="{
							path: `/pricing/apply/${item.pricelistId}`,
							query: { source: 'minmax' },
						}"
						color="primary"
						variant="text"
						icon
					>
						<v-icon size="15">fa: fal fa-arrow-right</v-icon>
					</v-btn>
				</template>
			</v-data-table>
		</v-card-text>
	</v-card>
</template>

<style scoped lang="scss">
.home-graph-card {
	display: flex;
	flex-direction: column;

	:deep(.v-table__wrapper) {
		height: 144px !important;
	}

	:deep(.v-data-table-footer) {
		margin-top: -0.5rem !important;
	}

	:deep(.v-data-table-footer__items-per-page) {
		display: none;
	}
}
</style>
